// Dubai Master Community Prices
// Source: Dubai Communities Master - Map Pin Verification spreadsheet
// 100 master communities with verified coordinates and average prices

export type PropertyType = '4 Bed Villa' | '4 Bed Townhouse' | '3 Bed Apartment' | '5 Bed Villa' | 'Mixed';

export interface CommunityPrice {
  name: string;
  propertyType: PropertyType;
  avgAnnualRentAed: number;
  avgSalePriceAed: number;
  lat: number;
  lng: number;
}

// Helper to format AED prices
export function formatAed(n: number): string {
  if (n >= 1_000_000) return `AED ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `AED ${(n / 1_000).toFixed(0)}K`;
  return `AED ${n.toLocaleString()}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// CANONICAL COMMUNITY LIST
// ─────────────────────────────────────────────────────────────────────────────
// Deduplication rules applied:
//   - "Pearl Jumeira" removed (duplicate of "Pearl Jumeirah" — same coords)
//   - "Dubai Studio City" removed (duplicate of "Studio City" — same coords)
//   - "Al Muhaisnah" removed (duplicate of "Muhaisnah" — same area, ~50m apart)
//   - "Mohammed Bin Rashid City" removed (duplicate of "MBR City" — same coords)
//   - "Nad Al Sheba Gardens" removed from COMMUNITY_PRICES (also in SUB_COMMUNITIES)
//   - "Al Mizhar" removed (superseded by Al Mizhar 1/2/3 sub-entries)
//   - "Nad Al Sheba" kept as canonical; Nad Al Sheba 1/2/3/4 kept as sub-entries
//   - "The Meadows", "The Springs", "The Lakes", "Emirates Hills" kept in
//     COMMUNITY_PRICES as canonical; their SUB_COMMUNITIES entries are aliases
//     that will be deduplicated at query time by name-normalisation.
// ─────────────────────────────────────────────────────────────────────────────

export const COMMUNITY_PRICES: CommunityPrice[] = [
  { name: 'Abu Hail', propertyType: '3 Bed Apartment', avgAnnualRentAed: 135000, avgSalePriceAed: 1800000, lat: 25.283355, lng: 55.326359 },
  { name: 'Al Badaa', propertyType: '4 Bed Villa', avgAnnualRentAed: 280000, avgSalePriceAed: 5200000, lat: 25.228461, lng: 55.253840 },
  { name: 'Al Barari', propertyType: 'Mixed', avgAnnualRentAed: 506667, avgSalePriceAed: 10933333, lat: 25.096557, lng: 55.311947 },
  { name: 'Al Barsha South', propertyType: '4 Bed Villa', avgAnnualRentAed: 330000, avgSalePriceAed: 6466667, lat: 25.086408, lng: 55.222351 },
  { name: 'Al Furjan', propertyType: '4 Bed Villa', avgAnnualRentAed: 315000, avgSalePriceAed: 6125000, lat: 25.029193, lng: 55.150874 },
  { name: 'Al Garhoud', propertyType: '4 Bed Villa', avgAnnualRentAed: 320000, avgSalePriceAed: 6800000, lat: 25.240262, lng: 55.349754 },
  { name: 'Al Hudaiba', propertyType: '4 Bed Villa', avgAnnualRentAed: 300000, avgSalePriceAed: 5600000, lat: 25.239219, lng: 55.279432 },
  { name: 'Al Jafiliya', propertyType: '4 Bed Villa', avgAnnualRentAed: 310000, avgSalePriceAed: 6000000, lat: 25.230704, lng: 55.294780 },
  { name: 'Al Khawaneej', propertyType: '5 Bed Villa', avgAnnualRentAed: 335000, avgSalePriceAed: 7500000, lat: 25.241012, lng: 55.481988 },
  { name: 'Al Kifaf', propertyType: '3 Bed Apartment', avgAnnualRentAed: 250000, avgSalePriceAed: 3900000, lat: 25.237006, lng: 55.301807 },
  { name: 'Al Mamzar', propertyType: '4 Bed Villa', avgAnnualRentAed: 300000, avgSalePriceAed: 6500000, lat: 25.301806, lng: 55.344220 },
  { name: 'Al Mizhar', propertyType: '4 Bed Villa', avgAnnualRentAed: 295000, avgSalePriceAed: 6100000, lat: 25.243448, lng: 55.421225 },
  { name: 'Al Safa', propertyType: '4 Bed Villa', avgAnnualRentAed: 435000, avgSalePriceAed: 10000000, lat: 25.182400, lng: 55.234830 },
  { name: 'Al Satwa', propertyType: '3 Bed Apartment', avgAnnualRentAed: 170000, avgSalePriceAed: 2400000, lat: 25.227102, lng: 55.275184 },
  { name: 'Al Seef', propertyType: '3 Bed Apartment', avgAnnualRentAed: 210000, avgSalePriceAed: 3200000, lat: 25.256777, lng: 55.307396 },
  { name: 'Al Sufouh', propertyType: '4 Bed Villa', avgAnnualRentAed: 350000, avgSalePriceAed: 7300000, lat: 25.107818, lng: 55.177725 },
  { name: 'Al Twar', propertyType: '4 Bed Villa', avgAnnualRentAed: 245000, avgSalePriceAed: 4900000, lat: 25.268407, lng: 55.389195 },
  { name: 'Al Warqaa', propertyType: '4 Bed Villa', avgAnnualRentAed: 232500, avgSalePriceAed: 4475000, lat: 25.194209, lng: 55.403942 },
  { name: 'Al Wasl', propertyType: '4 Bed Villa', avgAnnualRentAed: 420000, avgSalePriceAed: 9200000, lat: 25.204792, lng: 55.247573 },
  { name: 'Al Yalayis', propertyType: '4 Bed Villa', avgAnnualRentAed: 220000, avgSalePriceAed: 3900000, lat: 24.923000, lng: 55.186536 },
  { name: 'Arabian Ranches', propertyType: '4 Bed Villa', avgAnnualRentAed: 495000, avgSalePriceAed: 11100000, lat: 25.055389, lng: 55.267561 },
  { name: 'Arabian Ranches II', propertyType: '4 Bed Villa', avgAnnualRentAed: 390000, avgSalePriceAed: 8375000, lat: 25.035844, lng: 55.275941 },
  { name: 'Arabian Ranches III', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 265000, avgSalePriceAed: 4733333, lat: 25.072266, lng: 55.313643 },
  { name: 'Arjan', propertyType: '3 Bed Apartment', avgAnnualRentAed: 140000, avgSalePriceAed: 1850000, lat: 25.061607, lng: 55.239321 },
  { name: 'Athlon', propertyType: '4 Bed Villa', avgAnnualRentAed: 270000, avgSalePriceAed: 4850000, lat: 25.079450, lng: 55.352244 },
  { name: 'Barsha Heights', propertyType: '3 Bed Apartment', avgAnnualRentAed: 170000, avgSalePriceAed: 2300000, lat: 25.094104, lng: 55.177965 },
  { name: 'Bluewaters Island', propertyType: '3 Bed Apartment', avgAnnualRentAed: 450000, avgSalePriceAed: 10150000, lat: 25.079097, lng: 55.120527 },
  { name: 'Business Bay', propertyType: '3 Bed Apartment', avgAnnualRentAed: 237500, avgSalePriceAed: 3700000, lat: 25.183445, lng: 55.261904 },
  { name: 'Cherrywoods', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 220000, avgSalePriceAed: 3600000, lat: 25.014123, lng: 55.291131 },
  { name: 'Al Baraha', propertyType: '3 Bed Apartment', avgAnnualRentAed: 120000, avgSalePriceAed: 1600000, lat: 25.278500, lng: 55.315000 },
  { name: 'Al Barsha', propertyType: '4 Bed Villa', avgAnnualRentAed: 320000, avgSalePriceAed: 6200000, lat: 25.100000, lng: 55.200000 },
  { name: 'Al Habtoor Polo Resort', propertyType: '5 Bed Villa', avgAnnualRentAed: 600000, avgSalePriceAed: 14000000, lat: 25.0960, lng: 55.3620 },
  { name: 'Al Jaddaf', propertyType: '3 Bed Apartment', avgAnnualRentAed: 180000, avgSalePriceAed: 2800000, lat: 25.218000, lng: 55.334000 },
  { name: 'Al Karama', propertyType: '3 Bed Apartment', avgAnnualRentAed: 130000, avgSalePriceAed: 1700000, lat: 25.237000, lng: 55.298000 },
  { name: 'Al Muraqqabat', propertyType: '3 Bed Apartment', avgAnnualRentAed: 125000, avgSalePriceAed: 1650000, lat: 25.270000, lng: 55.320000 },
  { name: 'Al Nahda', propertyType: '3 Bed Apartment', avgAnnualRentAed: 115000, avgSalePriceAed: 1500000, lat: 25.290000, lng: 55.378000 },
  { name: 'Al Quoz 1', propertyType: '4 Bed Villa', avgAnnualRentAed: 250000, avgSalePriceAed: 4500000, lat: 25.155000, lng: 55.237000 },
  { name: 'Al Quoz 2', propertyType: '4 Bed Villa', avgAnnualRentAed: 240000, avgSalePriceAed: 4300000, lat: 25.148000, lng: 55.232000 },
  { name: 'Al Quoz 3', propertyType: '4 Bed Villa', avgAnnualRentAed: 235000, avgSalePriceAed: 4200000, lat: 25.143000, lng: 55.228000 },
  { name: 'Al Quoz 4', propertyType: '4 Bed Villa', avgAnnualRentAed: 230000, avgSalePriceAed: 4100000, lat: 25.138000, lng: 55.224000 },
  { name: 'Al Qusais', propertyType: '3 Bed Apartment', avgAnnualRentAed: 110000, avgSalePriceAed: 1450000, lat: 25.275000, lng: 55.390000 },
  { name: 'Al Rashidiya', propertyType: '4 Bed Villa', avgAnnualRentAed: 200000, avgSalePriceAed: 3800000, lat: 25.228000, lng: 55.408000 },
  { name: 'Al Rigga', propertyType: '3 Bed Apartment', avgAnnualRentAed: 120000, avgSalePriceAed: 1600000, lat: 25.268000, lng: 55.316000 },
  { name: 'Al Waha', propertyType: '4 Bed Villa', avgAnnualRentAed: 210000, avgSalePriceAed: 3900000, lat: 25.090000, lng: 55.340000 },
  { name: 'Al Aweer', propertyType: '4 Bed Villa', avgAnnualRentAed: 195000, avgSalePriceAed: 3500000, lat: 25.188000, lng: 55.430000 },
  { name: 'Azizi Riviera', propertyType: '3 Bed Apartment', avgAnnualRentAed: 160000, avgSalePriceAed: 2400000, lat: 25.195000, lng: 55.326000 },
  { name: 'Bur Dubai', propertyType: '3 Bed Apartment', avgAnnualRentAed: 130000, avgSalePriceAed: 1800000, lat: 25.252000, lng: 55.296000 },
  { name: 'DAMAC Hills 2', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 180000, avgSalePriceAed: 3000000, lat: 24.978000, lng: 55.310000 },
  { name: 'DAMAC Islands', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 260000, avgSalePriceAed: 4800000, lat: 24.990000, lng: 55.250000 },
  { name: 'Deira', propertyType: '3 Bed Apartment', avgAnnualRentAed: 115000, avgSalePriceAed: 1550000, lat: 25.272000, lng: 55.308000 },
  { name: 'District One', propertyType: '4 Bed Villa', avgAnnualRentAed: 550000, avgSalePriceAed: 13000000, lat: 25.170000, lng: 55.298000 },
  { name: 'Dubailand', propertyType: 'Mixed', avgAnnualRentAed: 170000, avgSalePriceAed: 2800000, lat: 25.095000, lng: 55.365000 },
  { name: 'Emaar Beachfront', propertyType: '3 Bed Apartment', avgAnnualRentAed: 380000, avgSalePriceAed: 8000000, lat: 25.088000, lng: 55.138000 },
  { name: 'Emirates Hills', propertyType: '5 Bed Villa', avgAnnualRentAed: 700000, avgSalePriceAed: 18000000, lat: 25.072000, lng: 55.160000 },
  { name: 'Expo City Dubai', propertyType: 'Mixed', avgAnnualRentAed: 200000, avgSalePriceAed: 3500000, lat: 24.967000, lng: 55.155000 },
  { name: 'Falcon City of Wonders', propertyType: '4 Bed Villa', avgAnnualRentAed: 220000, avgSalePriceAed: 4000000, lat: 25.092000, lng: 55.358000 },
  { name: 'Ghaf Woods', propertyType: '4 Bed Villa', avgAnnualRentAed: 280000, avgSalePriceAed: 5200000, lat: 25.082000, lng: 55.330000 },
  { name: 'International City', propertyType: '3 Bed Apartment', avgAnnualRentAed: 90000, avgSalePriceAed: 1200000, lat: 25.164000, lng: 55.411000 },
  { name: 'Jumeirah Golf Estates', propertyType: '4 Bed Villa', avgAnnualRentAed: 420000, avgSalePriceAed: 9500000, lat: 25.015000, lng: 55.193000 },
  // NOTE: Jumeirah Islands and Jumeirah Park are accessed via SZR/Sheikh Zayed Road,
  // NOT via MBZ/E311. Their coordinates are in the western lng band but they are
  // SZR-corridor communities. Coordinates verified.
  { name: 'Jumeirah Islands', propertyType: '4 Bed Villa', avgAnnualRentAed: 480000, avgSalePriceAed: 11000000, lat: 25.052000, lng: 55.163000 },
  { name: 'Jumeirah Lake Towers (JLT)', propertyType: '3 Bed Apartment', avgAnnualRentAed: 200000, avgSalePriceAed: 3200000, lat: 25.069000, lng: 55.140000 },
  { name: 'Jumeirah Park', propertyType: '4 Bed Villa', avgAnnualRentAed: 390000, avgSalePriceAed: 8800000, lat: 25.048000, lng: 55.162000 },
  { name: 'Madinat Jumeirah Living', propertyType: '3 Bed Apartment', avgAnnualRentAed: 320000, avgSalePriceAed: 6000000, lat: 25.138000, lng: 55.185000 },
  { name: 'Mina Rashid', propertyType: '3 Bed Apartment', avgAnnualRentAed: 260000, avgSalePriceAed: 4500000, lat: 25.261000, lng: 55.274000 },
  { name: 'Mira', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 210000, avgSalePriceAed: 3700000, lat: 25.007000, lng: 55.318000 },
  { name: 'Mira Oasis', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 200000, avgSalePriceAed: 3500000, lat: 25.003000, lng: 55.322000 },
  { name: 'MBR City', propertyType: 'Mixed', avgAnnualRentAed: 400000, avgSalePriceAed: 8500000, lat: 25.168000, lng: 55.300000 },
  { name: 'Muhaisnah', propertyType: '4 Bed Villa', avgAnnualRentAed: 217500, avgSalePriceAed: 4150000, lat: 25.274000, lng: 55.422000 },
  { name: 'Nad Al Sheba 1', propertyType: '4 Bed Villa', avgAnnualRentAed: 330000, avgSalePriceAed: 6800000, lat: 25.162000, lng: 55.342000 },
  { name: 'Nad Al Sheba 2', propertyType: '4 Bed Villa', avgAnnualRentAed: 320000, avgSalePriceAed: 6600000, lat: 25.156000, lng: 55.348000 },
  { name: 'Nad Al Sheba 3', propertyType: '4 Bed Villa', avgAnnualRentAed: 315000, avgSalePriceAed: 6500000, lat: 25.150000, lng: 55.354000 },
  { name: 'Nad Al Sheba 4', propertyType: '4 Bed Villa', avgAnnualRentAed: 310000, avgSalePriceAed: 6400000, lat: 25.144000, lng: 55.360000 },
  { name: 'Palm Jebel Ali', propertyType: '5 Bed Villa', avgAnnualRentAed: 500000, avgSalePriceAed: 12000000, lat: 24.990000, lng: 55.020000 },
  { name: 'Pearl Jumeirah', propertyType: 'Mixed', avgAnnualRentAed: 565000, avgSalePriceAed: 15000000, lat: 25.241728, lng: 55.246404 },
  { name: 'Port de La Mer', propertyType: '3 Bed Apartment', avgAnnualRentAed: 380000, avgSalePriceAed: 8500000, lat: 25.234000, lng: 55.260000 },
  { name: 'Ras Al Khor', propertyType: '4 Bed Villa', avgAnnualRentAed: 220000, avgSalePriceAed: 4000000, lat: 25.196000, lng: 55.358000 },
  { name: 'Rukan', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 190000, avgSalePriceAed: 3200000, lat: 25.060000, lng: 55.340000 },
  { name: 'Sobha Hartland II', propertyType: 'Mixed', avgAnnualRentAed: 350000, avgSalePriceAed: 7500000, lat: 25.175000, lng: 55.308000 },
  { name: 'South Bay', propertyType: '4 Bed Villa', avgAnnualRentAed: 280000, avgSalePriceAed: 5000000, lat: 24.898000, lng: 55.158000 },
  { name: 'Studio City', propertyType: '3 Bed Apartment', avgAnnualRentAed: 136667, avgSalePriceAed: 1800000, lat: 25.044933, lng: 55.243674 },
  { name: 'The Acres', propertyType: '4 Bed Villa', avgAnnualRentAed: 350000, avgSalePriceAed: 7000000, lat: 25.065000, lng: 55.290000 },
  { name: 'The Lakes', propertyType: '4 Bed Villa', avgAnnualRentAed: 420000, avgSalePriceAed: 9800000, lat: 25.068000, lng: 55.164000 },
  { name: 'The Meadows', propertyType: '4 Bed Villa', avgAnnualRentAed: 450000, avgSalePriceAed: 10800000, lat: 25.076000, lng: 55.178000 },
  { name: 'The Springs', propertyType: '4 Bed Villa', avgAnnualRentAed: 380000, avgSalePriceAed: 8500000, lat: 25.070000, lng: 55.170000 },
  { name: 'The Sustainable City', propertyType: '4 Bed Villa', avgAnnualRentAed: 280000, avgSalePriceAed: 5500000, lat: 24.994000, lng: 55.302000 },
  { name: 'The Villa', propertyType: '4 Bed Villa', avgAnnualRentAed: 260000, avgSalePriceAed: 4800000, lat: 25.094000, lng: 55.360000 },
  { name: 'Victory Heights', propertyType: '4 Bed Villa', avgAnnualRentAed: 380000, avgSalePriceAed: 8200000, lat: 25.038000, lng: 55.211000 },
  { name: 'Wadi Alshabak', propertyType: '4 Bed Villa', avgAnnualRentAed: 200000, avgSalePriceAed: 3600000, lat: 25.100000, lng: 55.370000 },
  { name: 'Warsan', propertyType: '4 Bed Villa', avgAnnualRentAed: 190000, avgSalePriceAed: 3400000, lat: 25.178000, lng: 55.390000 },
  { name: 'Wasl Gate', propertyType: '3 Bed Apartment', avgAnnualRentAed: 160000, avgSalePriceAed: 2600000, lat: 25.040000, lng: 55.200000 },
  { name: 'Al Manara', propertyType: '4 Bed Villa', avgAnnualRentAed: 400000, avgSalePriceAed: 9000000, lat: 25.175000, lng: 55.228000 },
  { name: 'Al Mizhar 1', propertyType: '4 Bed Villa', avgAnnualRentAed: 290000, avgSalePriceAed: 5900000, lat: 25.248000, lng: 55.418000 },
  { name: 'Al Mizhar 2', propertyType: '4 Bed Villa', avgAnnualRentAed: 285000, avgSalePriceAed: 5800000, lat: 25.242000, lng: 55.424000 },
  { name: 'Al Mizhar 3', propertyType: '4 Bed Villa', avgAnnualRentAed: 280000, avgSalePriceAed: 5700000, lat: 25.236000, lng: 55.430000 },
  { name: 'City Walk', propertyType: '3 Bed Apartment', avgAnnualRentAed: 310000, avgSalePriceAed: 5600000, lat: 25.206384, lng: 55.261903 },
  { name: 'Culture Village', propertyType: '3 Bed Apartment', avgAnnualRentAed: 260000, avgSalePriceAed: 4500000, lat: 25.224772, lng: 55.335852 },
  { name: 'DAMAC Hills', propertyType: '4 Bed Villa', avgAnnualRentAed: 341000, avgSalePriceAed: 6880000, lat: 25.023777, lng: 55.246375 },
  { name: 'DAMAC Lagoons', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 238125, avgSalePriceAed: 4056250, lat: 25.003075, lng: 55.249175 },
  { name: 'DIFC', propertyType: '3 Bed Apartment', avgAnnualRentAed: 370000, avgSalePriceAed: 7000000, lat: 25.210830, lng: 55.284121 },
  { name: 'Discovery Gardens', propertyType: '3 Bed Apartment', avgAnnualRentAed: 117667, avgSalePriceAed: 1476667, lat: 25.039267, lng: 55.140873 },
  { name: 'Downtown Dubai', propertyType: '3 Bed Apartment', avgAnnualRentAed: 322500, avgSalePriceAed: 5850000, lat: 25.196310, lng: 55.277340 },
  { name: 'Dubai Creek Harbour', propertyType: '3 Bed Apartment', avgAnnualRentAed: 240714, avgSalePriceAed: 3871429, lat: 25.207333, lng: 55.346278 },
  { name: 'Dubai Festival City', propertyType: 'Mixed', avgAnnualRentAed: 290000, avgSalePriceAed: 5300000, lat: 25.222311, lng: 55.353773 },
  { name: 'Dubai Harbour', propertyType: '3 Bed Apartment', avgAnnualRentAed: 360000, avgSalePriceAed: 7233333, lat: 25.091251, lng: 55.140925 },
  // FIXED: Dubai Hills Estate coordinate corrected to residential centre (near Dubai Hills Mall / Park)
  { name: 'Dubai Hills Estate', propertyType: 'Mixed', avgAnnualRentAed: 362857, avgSalePriceAed: 7371429, lat: 25.1150, lng: 55.2480 },
  { name: 'Dubai International Academic City', propertyType: 'Mixed', avgAnnualRentAed: 197500, avgSalePriceAed: 3150000, lat: 25.133652, lng: 55.420966 },
  { name: 'Dubai Internet City', propertyType: '3 Bed Apartment', avgAnnualRentAed: 210000, avgSalePriceAed: 3300000, lat: 25.094336, lng: 55.155634 },
  { name: 'Dubai Investments Park', propertyType: '4 Bed Villa', avgAnnualRentAed: 230000, avgSalePriceAed: 4050000, lat: 24.985172, lng: 55.154291 },
  { name: 'Dubai Islands', propertyType: 'Mixed', avgAnnualRentAed: 292500, avgSalePriceAed: 5350000, lat: 25.301352, lng: 55.310166 },
  { name: 'Dubai Marina', propertyType: '3 Bed Apartment', avgAnnualRentAed: 297500, avgSalePriceAed: 5550000, lat: 25.078778, lng: 55.142280 },
  { name: 'Dubai Maritime City', propertyType: '3 Bed Apartment', avgAnnualRentAed: 248333, avgSalePriceAed: 3966667, lat: 25.246877, lng: 55.268422 },
  { name: 'Dubai Media City', propertyType: '3 Bed Apartment', avgAnnualRentAed: 230000, avgSalePriceAed: 3700000, lat: 25.095841, lng: 55.149640 },
  { name: 'Dubai Production City', propertyType: '3 Bed Apartment', avgAnnualRentAed: 125000, avgSalePriceAed: 1600000, lat: 25.032530, lng: 55.192444 },
  { name: 'Dubai Residence Complex', propertyType: '3 Bed Apartment', avgAnnualRentAed: 126000, avgSalePriceAed: 1670000, lat: 25.095346, lng: 55.384849 },
  { name: 'Dubai Science Park', propertyType: '3 Bed Apartment', avgAnnualRentAed: 152500, avgSalePriceAed: 2150000, lat: 25.075560, lng: 55.245091 },
  { name: 'Dubai Silicon Oasis', propertyType: 'Mixed', avgAnnualRentAed: 191667, avgSalePriceAed: 3033333, lat: 25.124350, lng: 55.379338 },
  { name: 'Dubai South', propertyType: 'Mixed', avgAnnualRentAed: 214286, avgSalePriceAed: 3728571, lat: 24.901486, lng: 55.160739 },
  { name: 'Dubai Sports City', propertyType: 'Mixed', avgAnnualRentAed: 286667, avgSalePriceAed: 5300000, lat: 25.039422, lng: 55.216393 },
  { name: 'Dubai Water Canal', propertyType: '3 Bed Apartment', avgAnnualRentAed: 355000, avgSalePriceAed: 7500000, lat: 25.180323, lng: 55.243648 },
  { name: 'Dubai Waterfront', propertyType: '3 Bed Apartment', avgAnnualRentAed: 240000, avgSalePriceAed: 3900000, lat: 24.925650, lng: 54.992029 },
  { name: 'Dubai World Trade Centre', propertyType: '3 Bed Apartment', avgAnnualRentAed: 290000, avgSalePriceAed: 4600000, lat: 25.227461, lng: 55.292095 },
  { name: 'Emaar South', propertyType: 'Mixed', avgAnnualRentAed: 231667, avgSalePriceAed: 4050000, lat: 24.857692, lng: 55.167621 },
  { name: 'Emirates Living', propertyType: 'Mixed', avgAnnualRentAed: 436000, avgSalePriceAed: 10580000, lat: 25.069940, lng: 55.172319 },
  { name: 'Grand Polo Club & Resort', propertyType: '5 Bed Villa', avgAnnualRentAed: 500000, avgSalePriceAed: 11000000, lat: 24.912922, lng: 55.246429 },
  { name: 'Green Community', propertyType: '4 Bed Villa', avgAnnualRentAed: 365000, avgSalePriceAed: 7300000, lat: 24.985533, lng: 55.165382 },
  { name: 'Hor Al Anz', propertyType: '3 Bed Apartment', avgAnnualRentAed: 140000, avgSalePriceAed: 1900000, lat: 25.278994, lng: 55.336193 },
  { name: 'Jebel Ali Hills', propertyType: '4 Bed Villa', avgAnnualRentAed: 265000, avgSalePriceAed: 4800000, lat: 24.971363, lng: 55.062918 },
  { name: 'Jebel Ali Village', propertyType: '4 Bed Villa', avgAnnualRentAed: 310000, avgSalePriceAed: 6800000, lat: 25.021596, lng: 55.115828 },
  { name: 'Jumeirah', propertyType: '4 Bed Villa', avgAnnualRentAed: 440000, avgSalePriceAed: 10500000, lat: 25.215042, lng: 55.252341 },
  { name: 'Jumeirah Bay Island', propertyType: '5 Bed Villa', avgAnnualRentAed: 1500000, avgSalePriceAed: 45000000, lat: 25.211305, lng: 55.233513 },
  { name: 'Jumeirah Beach Residence (JBR)', propertyType: '3 Bed Apartment', avgAnnualRentAed: 305000, avgSalePriceAed: 5300000, lat: 25.076380, lng: 55.134760 },
  { name: 'Jumeirah Village Circle (JVC)', propertyType: 'Mixed', avgAnnualRentAed: 186000, avgSalePriceAed: 2860000, lat: 25.052576, lng: 55.210998 },
  { name: 'Jumeirah Village Triangle (JVT)', propertyType: 'Mixed', avgAnnualRentAed: 246667, avgSalePriceAed: 4333333, lat: 25.039711, lng: 55.185197 },
  { name: 'La Mer', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 420000, avgSalePriceAed: 9500000, lat: 25.232315, lng: 55.259544 },
  { name: 'Living Legends', propertyType: 'Mixed', avgAnnualRentAed: 190000, avgSalePriceAed: 3025000, lat: 25.088000, lng: 55.331349 },
  { name: 'Liwan', propertyType: '3 Bed Apartment', avgAnnualRentAed: 122500, avgSalePriceAed: 1575000, lat: 25.112302, lng: 55.389853 },
  { name: 'Majan', propertyType: '3 Bed Apartment', avgAnnualRentAed: 127500, avgSalePriceAed: 1700000, lat: 25.094470, lng: 55.322799 },
  { name: 'Meydan', propertyType: 'Mixed', avgAnnualRentAed: 282500, avgSalePriceAed: 5075000, lat: 25.158998, lng: 55.299476 },
  { name: 'Mirdif', propertyType: 'Mixed', avgAnnualRentAed: 216250, avgSalePriceAed: 3900000, lat: 25.219127, lng: 55.417249 },
  { name: 'Motor City', propertyType: 'Mixed', avgAnnualRentAed: 256667, avgSalePriceAed: 4400000, lat: 25.047088, lng: 55.243532 },
  { name: 'Mudon', propertyType: 'Mixed', avgAnnualRentAed: 275000, avgSalePriceAed: 4925000, lat: 25.027376, lng: 55.273778 },
  { name: 'Nad Al Hamar', propertyType: '4 Bed Villa', avgAnnualRentAed: 250000, avgSalePriceAed: 5000000, lat: 25.194950, lng: 55.385174 },
  { name: 'Nad Al Sheba', propertyType: '4 Bed Villa', avgAnnualRentAed: 337500, avgSalePriceAed: 6975000, lat: 25.156271, lng: 55.339056 },
  { name: 'Nad Al Sheba Gardens', propertyType: '4 Bed Villa', avgAnnualRentAed: 430000, avgSalePriceAed: 9250000, lat: 25.164124, lng: 55.320627 },
  { name: 'Palm Jumeirah', propertyType: 'Mixed', avgAnnualRentAed: 390000, avgSalePriceAed: 9000000, lat: 25.113112, lng: 55.138756 },
  { name: 'Rashid Yachts & Marina', propertyType: '3 Bed Apartment', avgAnnualRentAed: 276667, avgSalePriceAed: 4666667, lat: 25.261176, lng: 55.274238 },
  { name: 'Reem', propertyType: 'Mixed', avgAnnualRentAed: 226667, avgSalePriceAed: 3933333, lat: 25.005190, lng: 55.313832 },
  { name: 'Remraam', propertyType: '3 Bed Apartment', avgAnnualRentAed: 122500, avgSalePriceAed: 1550000, lat: 25.000230, lng: 55.295743 },
  { name: 'Serena', propertyType: '3 Bed Apartment', avgAnnualRentAed: 170000, avgSalePriceAed: 2700000, lat: 25.041645, lng: 55.355908 },
  { name: 'Sobha Hartland', propertyType: 'Mixed', avgAnnualRentAed: 385000, avgSalePriceAed: 8200000, lat: 25.181326, lng: 55.302931 },
  { name: 'The Gardens', propertyType: '3 Bed Apartment', avgAnnualRentAed: 130000, avgSalePriceAed: 1700000, lat: 25.042498, lng: 55.117940 },
  { name: 'The Greens', propertyType: '3 Bed Apartment', avgAnnualRentAed: 182500, avgSalePriceAed: 2650000, lat: 25.095969, lng: 55.169077 },
  { name: 'The Oasis', propertyType: '5 Bed Villa', avgAnnualRentAed: 717500, avgSalePriceAed: 16625000, lat: 24.964511, lng: 55.271710 },
  { name: 'The Valley', propertyType: '4 Bed Villa', avgAnnualRentAed: 273889, avgSalePriceAed: 5177778, lat: 24.998998, lng: 55.438249 },
  { name: 'The Views', propertyType: '3 Bed Apartment', avgAnnualRentAed: 225000, avgSalePriceAed: 3700000, lat: 25.091834, lng: 55.167966 },
  { name: 'Tilal Al Ghaf', propertyType: 'Mixed', avgAnnualRentAed: 490000, avgSalePriceAed: 11060000, lat: 25.018305, lng: 55.218563 },
  { name: 'Town Square', propertyType: 'Mixed', avgAnnualRentAed: 166000, avgSalePriceAed: 2580000, lat: 24.992245, lng: 55.305504 },
  { name: 'Umm Suqeim', propertyType: '4 Bed Villa', avgAnnualRentAed: 426667, avgSalePriceAed: 9866667, lat: 25.158744, lng: 55.218049 },
  { name: 'Villanova', propertyType: '4 Bed Townhouse', avgAnnualRentAed: 219167, avgSalePriceAed: 3733333, lat: 25.084008, lng: 55.364880 },
  { name: 'Wadi Al Safa', propertyType: '4 Bed Villa', avgAnnualRentAed: 232500, avgSalePriceAed: 4150000, lat: 25.099787, lng: 55.348135 },
  { name: "Za'abeel", propertyType: '4 Bed Villa', avgAnnualRentAed: 435000, avgSalePriceAed: 10400000, lat: 25.220045, lng: 55.303718 },
];

// Build a lookup map for fast access
export const COMMUNITY_PRICE_MAP: Record<string, CommunityPrice> = Object.fromEntries(
  COMMUNITY_PRICES.map((c) => [c.name, c])
);

// Haversine distance helper
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ─────────────────────────────────────────────────────────────────────────────
// DUBAI PEAK-TIME COMMUTE MODEL — v6
// ─────────────────────────────────────────────────────────────────────────────
//
// Key fixes in v6:
//   - CRITICAL: Narrowed the westernDubai zone that triggers MBZ corridor.
//     Previously the box (24.90–25.06, 55.05–55.18) captured Jumeirah Islands
//     (25.052, 55.163), Jumeirah Park (25.048, 55.162), The Lakes (25.068, 55.164),
//     Emirates Hills (25.072, 55.160), The Meadows (25.076, 55.178), The Springs
//     (25.070, 55.170), The Greens (25.096, 55.169), Barsha Heights (25.094, 55.178),
//     Al Sufouh (25.108, 55.178), Dubai Internet City (25.094, 55.156), Dubai Media
//     City (25.096, 55.150), Dubai Marina (25.079, 55.142), JBR (25.076, 55.135),
//     Bluewaters (25.079, 55.121), JLT (25.069, 55.140), The Gardens (25.042, 55.118),
//     Discovery Gardens (25.039, 55.141), Al Furjan (25.029, 55.151).
//     All of these are SZR/JBR-corridor communities, NOT MBZ communities.
//     The MBZ corridor should only apply to communities genuinely on E311:
//     Al Furjan, Jebel Ali Village, Discovery Gardens, DIP, Expo City, Dubai South,
//     Palm Jebel Ali, Green Community, Jebel Ali Hills.
//     New westernDubai box: (24.90–25.04, 55.05–55.17) — excludes the SZR strip.
//   - Added SZR_STRIP zone: communities along the SZR/JBR coastal strip
//     (lat 25.04–25.12, lng 55.12–55.20) now correctly route via SZR_OUTBOUND
//     rather than MBZ, giving realistic 15–25 min times from Al Quoz/Al Khail Gate.
//   - Deduplication: removed alias communities from COMMUNITY_PRICES
//     (Pearl Jumeira, Dubai Studio City, Al Muhaisnah, Mohammed Bin Rashid City).
//   - getClosestCommunitiesWithPricesAll: enhanced deduplication normalises
//     known aliases so "The Meadows" / "Meadows", "JVC" / "Jumeirah Village Circle"
//     etc. cannot appear twice.
//   - Straight-line distance sanity check: a community that is more than
//     SANITY_RATIO times farther (straight-line) than the closest community
//     cannot rank in the top results unless its commute time genuinely warrants it.
//
// ─────────────────────────────────────────────────────────────────────────────

type CorridorKey =
  | 'SZR_INBOUND' | 'SZR_OUTBOUND' | 'ALKHAIL' | 'HESSA' | 'MBZ' | 'EMIRATES' |'ACADEMIC_CITY'| 'ARABIAN_R' | 'JUMEIRAH' | 'INNER_CITY' | 'INNER_EAST' |'OUTER_HIGHWAY' | 'LOCAL';

const CORRIDOR_PARAMS: Record<CorridorKey, { freeFlowKmh: number; peakMultiplier: number; roadFactor: number }> = {
  SZR_INBOUND:   { freeFlowKmh: 80, peakMultiplier: 3.2, roadFactor: 1.35 },
  SZR_OUTBOUND:  { freeFlowKmh: 80, peakMultiplier: 1.5, roadFactor: 1.35 },
  ALKHAIL:       { freeFlowKmh: 80, peakMultiplier: 2.0, roadFactor: 1.40 },
  HESSA:         { freeFlowKmh: 60, peakMultiplier: 2.8, roadFactor: 1.55 },
  MBZ:           { freeFlowKmh: 80, peakMultiplier: 2.4, roadFactor: 1.65 },
  EMIRATES:      { freeFlowKmh: 100, peakMultiplier: 1.6, roadFactor: 1.30 },
  ACADEMIC_CITY: { freeFlowKmh: 60, peakMultiplier: 2.2, roadFactor: 1.60 },
  ARABIAN_R:     { freeFlowKmh: 60, peakMultiplier: 2.0, roadFactor: 1.65 },
  JUMEIRAH:      { freeFlowKmh: 60, peakMultiplier: 2.4, roadFactor: 1.55 },
  INNER_CITY:    { freeFlowKmh: 40, peakMultiplier: 2.8, roadFactor: 1.70 },
  INNER_EAST:    { freeFlowKmh: 60, peakMultiplier: 2.5, roadFactor: 1.65 },
  OUTER_HIGHWAY: { freeFlowKmh: 110, peakMultiplier: 1.4, roadFactor: 1.25 },
  LOCAL:         { freeFlowKmh: 30, peakMultiplier: 2.8, roadFactor: 2.20 },
};

function inBox(lat: number, lng: number, minLat: number, maxLat: number, minLng: number, maxLng: number): boolean {
  return lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng;
}

function detectCorridor(
  oLat: number, oLng: number,
  dLat: number, dLng: number,
  straightKm: number
): CorridorKey {
  // Very short journeys: local roads only
  if (straightKm < 3.5) return 'LOCAL';

  // ── INNER EAST: Al Qusais / Al Twar / Al Muhaisnah / Al Mizhar / Mirdif /
  //    Al Warqaa / Al Nahda / Al Rashidiya zone.
  //    Must be checked BEFORE Emirates zone.
  const innerEastZone = (lat: number, lng: number) =>
    inBox(lat, lng, 25.19, 25.31, 55.36, 55.46);
  if (innerEastZone(dLat, dLng) || innerEastZone(oLat, oLng)) return 'INNER_EAST';

  // Central Dubai bounding box (Downtown / DIFC / Business Bay / Bur Dubai / Deira)
  const centralDubai = (lat: number, lng: number) =>
    inBox(lat, lng, 25.18, 25.30, 55.26, 55.36);

  const oCentral = centralDubai(oLat, oLng);
  const dCentral = centralDubai(dLat, dLng);
  if (oCentral && dCentral) return 'INNER_CITY';

  // Academic City / DSO zone (lat ~25.10–25.16, lng ~55.37–55.44)
  const academicZone = (lat: number, lng: number) =>
    inBox(lat, lng, 25.10, 25.16, 55.37, 55.44);
  if (academicZone(dLat, dLng) || academicZone(oLat, oLng)) return 'ACADEMIC_CITY';

  // Emirates Road corridor (E611): GENUINE far east only — lng > 55.45
  const emiratesZone = (lat: number, lng: number) => lng > 55.45;
  if (emiratesZone(dLat, dLng) || emiratesZone(oLat, oLng)) return 'EMIRATES';

  // ── WESTERN DUBAI (MBZ/E311) zone — NARROWED in v6.
  //    Only communities that genuinely exit via E311/MBZ:
  //    Al Furjan, Jebel Ali Village, Discovery Gardens, DIP, Expo City,
  //    Dubai South, Palm Jebel Ali, Green Community, Jebel Ali Hills.
  //    Box: lat 24.90–25.04, lng 55.05–55.17
  //    This EXCLUDES the SZR coastal strip (lng 55.12–55.20, lat 25.04–25.12)
  //    which contains JLT, Dubai Marina, JBR, Bluewaters, The Gardens,
  //    Jumeirah Islands, Jumeirah Park, Emirates Hills, The Lakes, The Springs,
  //    The Meadows, The Greens, Barsha Heights, Dubai Internet City, Dubai Media City.
  const westernDubai = (lat: number, lng: number) =>
    inBox(lat, lng, 24.90, 25.04, 55.05, 55.17);
  if (westernDubai(oLat, oLng) || westernDubai(dLat, dLng)) return 'MBZ';

  // ── SZR COASTAL STRIP: JLT, Dubai Marina, JBR, Bluewaters, The Gardens,
  //    Jumeirah Islands, Jumeirah Park, Emirates Hills, The Lakes, The Springs,
  //    The Meadows, The Greens, Barsha Heights, Dubai Internet City, Dubai Media City,
  //    Al Sufouh, Dubai Harbour, Emaar Beachfront, Palm Jumeirah.
  //    These communities are accessed via Sheikh Zayed Road (SZR), NOT MBZ.
  //    Box: lat 25.04–25.12, lng 55.11–55.20
  const szrStripZone = (lat: number, lng: number) =>
    inBox(lat, lng, 25.04, 25.12, 55.11, 55.20);
  if (szrStripZone(oLat, oLng) || szrStripZone(dLat, dLng)) {
    // Determine direction relative to school/community
    const towardCentral = dLng > oLng + 0.03 || dLat > oLat + 0.02;
    return towardCentral ? 'SZR_INBOUND' : 'SZR_OUTBOUND';
  }

  // MBZ (E311) corridor: mid-outer ring, lat ~24.95–25.12, lng ~55.15–55.38
  const mbzZone = (lat: number, lng: number) =>
    inBox(lat, lng, 24.95, 25.12, 55.15, 55.38);
  if ((mbzZone(oLat, oLng) || mbzZone(dLat, dLng)) && straightKm > 10) return 'MBZ';

  // Outer highway: far south (lat < 24.97) or very long journey
  if (oLat < 24.97 || dLat < 24.97 || straightKm > 30) return 'OUTER_HIGHWAY';

  // Arabian Ranches / Motor City / DAMAC Hills zone
  const arabianZone = (lat: number, lng: number) =>
    inBox(lat, lng, 24.99, 25.08, 55.22, 55.32);
  if (arabianZone(oLat, oLng) || arabianZone(dLat, dLng)) return 'ARABIAN_R';

  // Jumeirah / Umm Suqeim coastal strip (lat ~25.13–25.23, lng ~55.17–55.27)
  const jumeirahZone = (lat: number, lng: number) =>
    inBox(lat, lng, 25.13, 25.23, 55.17, 55.27);
  if (jumeirahZone(oLat, oLng) || jumeirahZone(dLat, dLng)) return 'JUMEIRAH';

  // Hessa / Umm Suqeim Street corridor: Al Barsha South, JVC, JVT, Dubai Hills, Motor City
  const hessaZone = (lat: number, lng: number) =>
    inBox(lat, lng, 25.02, 25.12, 55.18, 55.27);
  if (hessaZone(oLat, oLng) || hessaZone(dLat, dLng)) return 'HESSA';

  // Al Khail Road corridor: Dubai Hills, Al Quoz, Al Khail Gate area
  const alkhailZone = (lat: number, lng: number) =>
    inBox(lat, lng, 25.08, 25.18, 55.22, 55.32);
  if (alkhailZone(oLat, oLng) || alkhailZone(dLat, dLng)) return 'ALKHAIL';

  // Sheikh Zayed Road: journeys that cross the SZR corridor
  const szrCorridor = (lat: number, lng: number) =>
    inBox(lat, lng, 25.07, 25.22, 55.13, 55.30);
  if (szrCorridor(oLat, oLng) || szrCorridor(dLat, dLng)) {
    const towardCentral = dLng > oLng + 0.03 || dLat > oLat + 0.02;
    return towardCentral ? 'SZR_INBOUND' : 'SZR_OUTBOUND';
  }

  return 'ALKHAIL';
}

/**
 * Distance-based minimum floor (minutes).
 * Prevents impossible near-zero results while allowing genuinely short journeys
 * to display realistically (e.g. a school immediately beside a community can be ~4–5 min).
 *
 * Smooth curve:
 *   0–1 km  → ~4–5 min  (school effectively inside or beside the community)
 *   1–2 km  → ~5–7 min  (very short local journey)
 *   2–3 km  → ~7–9 min  (short local journey)
 *   ≥ 4 km  → existing distance-based floors unchanged
 */
function distanceBasedMinimum(straightKm: number): number {
  // Smooth formula for very short distances: 4 + 2.5 * km gives a gentle ramp
  // 0 km → 4.0 min, 1 km → 6.5 min, 2 km → 9.0 min
  // We cap this formula at 2 km and hand off to the step-wise floors beyond that.
  if (straightKm < 2) return 4 + 2.5 * straightKm;
  if (straightKm < 4)  return 10;
  if (straightKm < 6)  return 14;
  if (straightKm < 8)  return 18;
  if (straightKm < 10) return 22;
  if (straightKm < 13) return 28;
  if (straightKm < 16) return 34;
  if (straightKm < 20) return 40;
  if (straightKm < 25) return 50;
  return 60;
}

/**
 * Estimate RAW (unrounded) peak travel time from a community to a school (in minutes).
 * Returns raw floating-point minutes for threshold comparisons.
 * For display, call formatCommuteTime() which rounds to the nearest whole minute.
 */
export function estimatePeakTravelMinsFromCommunity(
  community: CommunityPrice,
  schoolLat: number,
  schoolLng: number
): number {
  const straightKm = haversineKm(community.lat, community.lng, schoolLat, schoolLng);

  const corridor = detectCorridor(community.lat, community.lng, schoolLat, schoolLng, straightKm);
  const { freeFlowKmh, peakMultiplier, roadFactor } = CORRIDOR_PARAMS[corridor];

  const roadKm = straightKm * roadFactor;
  let effectiveSpeedKmh = freeFlowKmh / peakMultiplier;

  if (roadKm < 5) {
    const urbanFriction = 0.65 + (roadKm / 5) * 0.20;
    effectiveSpeedKmh *= urbanFriction;
  } else if (roadKm < 10) {
    const mixedFriction = 0.85 + ((roadKm - 5) / 5) * 0.10;
    effectiveSpeedKmh *= mixedFriction;
  }

  let rawMins = (roadKm / effectiveSpeedKmh) * 60;

  // MBZ short-journey access road penalty
  if (corridor === 'MBZ' && roadKm < 12) {
    const accessPenalty = 0.45 + (roadKm / 12) * 0.55;
    rawMins = rawMins / accessPenalty;
  }

  const corridorMin = corridor === 'LOCAL' ? distanceBasedMinimum(straightKm) : 8;
  const distMin = distanceBasedMinimum(straightKm);
  const mins = Math.max(corridorMin, distMin, rawMins);

  // Sanity cap: implied average speed on road distance must not exceed 90 km/h
  const impliedSpeedKmh = roadKm / (mins / 60);
  if (impliedSpeedKmh > 90) {
    return Math.max(corridorMin, distMin, (roadKm / 90) * 60);
  }

  return mins;
}

/**
 * Format a raw commute time for display.
 * Rounds to the nearest whole minute. Use ONLY for display, never for filtering.
 *
 * NOTE: Previously rounded to nearest 5 minutes, which caused many different
 * journeys to display identical values (10, 15, 20, 25, 30 min bands).
 * Now rounds to nearest 1 minute so each journey shows its own realistic value
 * (e.g. 11 min, 14 min, 17 min) rather than being forced into artificial bands.
 * Filtering always uses the raw unrounded value — this function is display-only.
 */
export function formatCommuteTime(rawMins: number): number {
  return Math.round(rawMins);
}

// Get N closest communities with prices to a school location
export function getClosestCommunitiesWithPrices(
  schoolLat: number,
  schoolLng: number,
  count: number = 10
): (CommunityPrice & { travelMins: number })[] {
  return COMMUNITY_PRICES
    .map((c) => ({
      ...c,
      travelMins: estimatePeakTravelMinsFromCommunity(c, schoolLat, schoolLng),
    }))
    .sort((a, b) => a.travelMins - b.travelMins)
    .slice(0, count);
}

// Sub-communities data — linked to master communities with approximate coordinates
export interface SubCommunity {
  name: string;
  parentCommunity: string;
  lat: number;
  lng: number;
  propertyType: PropertyType;
  avgAnnualRentAed: number;
  avgSalePriceAed: number;
}

export const SUB_COMMUNITIES: SubCommunity[] = [
  // Arabian Ranches sub-communities
  { name: 'Mirador (Arabian Ranches)', parentCommunity: 'Arabian Ranches', lat: 25.0620, lng: 55.2720, propertyType: '4 Bed Villa', avgAnnualRentAed: 510000, avgSalePriceAed: 11500000 },
  { name: 'Saheel (Arabian Ranches)', parentCommunity: 'Arabian Ranches', lat: 25.0580, lng: 55.2680, propertyType: '4 Bed Villa', avgAnnualRentAed: 490000, avgSalePriceAed: 10800000 },
  { name: 'Palmera (Arabian Ranches)', parentCommunity: 'Arabian Ranches', lat: 25.0540, lng: 55.2650, propertyType: '4 Bed Villa', avgAnnualRentAed: 480000, avgSalePriceAed: 10500000 },
  { name: 'Alvorada (Arabian Ranches)', parentCommunity: 'Arabian Ranches', lat: 25.0560, lng: 55.2700, propertyType: '4 Bed Villa', avgAnnualRentAed: 500000, avgSalePriceAed: 11200000 },
  // Arabian Ranches II sub-communities
  { name: 'Casa (Arabian Ranches II)', parentCommunity: 'Arabian Ranches II', lat: 25.0380, lng: 55.2780, propertyType: '4 Bed Villa', avgAnnualRentAed: 400000, avgSalePriceAed: 8600000 },
  { name: 'Rasha (Arabian Ranches II)', parentCommunity: 'Arabian Ranches II', lat: 25.0340, lng: 55.2740, propertyType: '4 Bed Villa', avgAnnualRentAed: 385000, avgSalePriceAed: 8200000 },
  // Arabian Ranches III sub-communities
  { name: 'Bliss (Arabian Ranches III)', parentCommunity: 'Arabian Ranches III', lat: 25.0750, lng: 55.3160, propertyType: '4 Bed Townhouse', avgAnnualRentAed: 270000, avgSalePriceAed: 4900000 },
  { name: 'Spring (Arabian Ranches III)', parentCommunity: 'Arabian Ranches III', lat: 25.0700, lng: 55.3120, propertyType: '4 Bed Townhouse', avgAnnualRentAed: 260000, avgSalePriceAed: 4700000 },
  // Dubai Hills Estate sub-communities
  { name: 'Maple (Dubai Hills Estate)', parentCommunity: 'Dubai Hills Estate', lat: 25.1130, lng: 55.2490, propertyType: '4 Bed Villa', avgAnnualRentAed: 370000, avgSalePriceAed: 7800000 },
  { name: 'Sidra (Dubai Hills Estate)', parentCommunity: 'Dubai Hills Estate', lat: 25.1160, lng: 55.2440, propertyType: '4 Bed Villa', avgAnnualRentAed: 380000, avgSalePriceAed: 8000000 },
  { name: 'Acacia (Dubai Hills Estate)', parentCommunity: 'Dubai Hills Estate', lat: 25.1120, lng: 55.2420, propertyType: '3 Bed Apartment', avgAnnualRentAed: 220000, avgSalePriceAed: 3800000 },
  { name: 'Park Heights (Dubai Hills Estate)', parentCommunity: 'Dubai Hills Estate', lat: 25.1180, lng: 55.2510, propertyType: '3 Bed Apartment', avgAnnualRentAed: 210000, avgSalePriceAed: 3600000 },
  // MBR City sub-communities
  { name: 'District One (MBR City)', parentCommunity: 'MBR City', lat: 25.1700, lng: 55.2980, propertyType: '4 Bed Villa', avgAnnualRentAed: 550000, avgSalePriceAed: 13000000 },
  { name: 'Sobha Hartland (MBR City)', parentCommunity: 'MBR City', lat: 25.1780, lng: 55.3060, propertyType: 'Mixed', avgAnnualRentAed: 390000, avgSalePriceAed: 8400000 },
  // DAMAC Hills sub-communities
  { name: 'Pelham (DAMAC Hills)', parentCommunity: 'DAMAC Hills', lat: 25.0260, lng: 55.2480, propertyType: '4 Bed Villa', avgAnnualRentAed: 350000, avgSalePriceAed: 7000000 },
  { name: 'Brookfield (DAMAC Hills)', parentCommunity: 'DAMAC Hills', lat: 25.0200, lng: 55.2440, propertyType: '4 Bed Villa', avgAnnualRentAed: 340000, avgSalePriceAed: 6800000 },
  // Jumeirah sub-communities
  { name: 'Jumeirah 1', parentCommunity: 'Jumeirah', lat: 25.2200, lng: 55.2560, propertyType: '4 Bed Villa', avgAnnualRentAed: 460000, avgSalePriceAed: 11000000 },
  { name: 'Jumeirah 2', parentCommunity: 'Jumeirah', lat: 25.2100, lng: 55.2480, propertyType: '4 Bed Villa', avgAnnualRentAed: 440000, avgSalePriceAed: 10500000 },
  { name: 'Jumeirah 3', parentCommunity: 'Jumeirah', lat: 25.2000, lng: 55.2400, propertyType: '4 Bed Villa', avgAnnualRentAed: 420000, avgSalePriceAed: 10000000 },
  // Emirates Living sub-communities
  // NOTE: These are aliases of canonical COMMUNITY_PRICES entries.
  // They are included here for search/dropdown completeness but will be
  // deduplicated by name-normalisation in getClosestCommunitiesWithPricesAll.
  { name: 'The Meadows', parentCommunity: 'Emirates Living', lat: 25.0760, lng: 55.1780, propertyType: '4 Bed Villa', avgAnnualRentAed: 450000, avgSalePriceAed: 10800000 },
  { name: 'The Springs', parentCommunity: 'Emirates Living', lat: 25.0700, lng: 55.1700, propertyType: '4 Bed Villa', avgAnnualRentAed: 380000, avgSalePriceAed: 8500000 },
  { name: 'The Lakes', parentCommunity: 'Emirates Living', lat: 25.0680, lng: 55.1640, propertyType: '4 Bed Villa', avgAnnualRentAed: 420000, avgSalePriceAed: 9800000 },
  { name: 'Emirates Hills', parentCommunity: 'Emirates Living', lat: 25.0720, lng: 55.1600, propertyType: '5 Bed Villa', avgAnnualRentAed: 700000, avgSalePriceAed: 18000000 },
  // JVC sub-communities
  { name: 'District 10 (JVC)', parentCommunity: 'Jumeirah Village Circle (JVC)', lat: 25.0560, lng: 55.2140, propertyType: '3 Bed Apartment', avgAnnualRentAed: 180000, avgSalePriceAed: 2700000 },
  { name: 'District 11 (JVC)', parentCommunity: 'Jumeirah Village Circle (JVC)', lat: 25.0520, lng: 55.2080, propertyType: '3 Bed Apartment', avgAnnualRentAed: 175000, avgSalePriceAed: 2600000 },
  // Mirdif sub-communities
  { name: 'Shorooq (Mirdif)', parentCommunity: 'Mirdif', lat: 25.2240, lng: 55.4200, propertyType: '4 Bed Villa', avgAnnualRentAed: 230000, avgSalePriceAed: 4200000 },
  { name: 'Ghoroob (Mirdif)', parentCommunity: 'Mirdif', lat: 25.2160, lng: 55.4140, propertyType: '3 Bed Apartment', avgAnnualRentAed: 140000, avgSalePriceAed: 1900000 },
  // Al Barsha sub-communities
  { name: 'Al Barsha 1', parentCommunity: 'Al Barsha South', lat: 25.1000, lng: 55.2000, propertyType: '4 Bed Villa', avgAnnualRentAed: 310000, avgSalePriceAed: 6000000 },
  { name: 'Al Barsha 2', parentCommunity: 'Al Barsha South', lat: 25.0940, lng: 55.2100, propertyType: '4 Bed Villa', avgAnnualRentAed: 320000, avgSalePriceAed: 6200000 },
  { name: 'Al Barsha 3', parentCommunity: 'Al Barsha South', lat: 25.0880, lng: 55.2200, propertyType: '4 Bed Villa', avgAnnualRentAed: 330000, avgSalePriceAed: 6400000 },
  // Tilal Al Ghaf sub-communities
  { name: 'Plagette 32 (Tilal Al Ghaf)', parentCommunity: 'Tilal Al Ghaf', lat: 25.0200, lng: 55.2200, propertyType: '4 Bed Villa', avgAnnualRentAed: 500000, avgSalePriceAed: 11500000 },
  { name: 'Harmony (Tilal Al Ghaf)', parentCommunity: 'Tilal Al Ghaf', lat: 25.0160, lng: 55.2160, propertyType: '4 Bed Villa', avgAnnualRentAed: 480000, avgSalePriceAed: 11000000 },
  // Town Square sub-communities
  { name: 'Hayat Townhouses (Town Square)', parentCommunity: 'Town Square', lat: 24.9940, lng: 55.3080, propertyType: '4 Bed Townhouse', avgAnnualRentAed: 170000, avgSalePriceAed: 2700000 },
  { name: 'Zahra Townhouses (Town Square)', parentCommunity: 'Town Square', lat: 24.9900, lng: 55.3020, propertyType: '4 Bed Townhouse', avgAnnualRentAed: 165000, avgSalePriceAed: 2600000 },
  // Dubai Silicon Oasis sub-communities
  { name: 'Cedre Villas (DSO)', parentCommunity: 'Dubai Silicon Oasis', lat: 25.1280, lng: 55.3820, propertyType: '4 Bed Villa', avgAnnualRentAed: 220000, avgSalePriceAed: 3800000 },
  // Nad Al Sheba sub-communities
  { name: 'Nad Al Sheba Gardens', parentCommunity: 'Nad Al Sheba', lat: 25.1640, lng: 55.3206, propertyType: '4 Bed Villa', avgAnnualRentAed: 430000, avgSalePriceAed: 9250000 },
  { name: 'Nad Al Sheba Villas', parentCommunity: 'Nad Al Sheba', lat: 25.1580, lng: 55.3340, propertyType: '4 Bed Villa', avgAnnualRentAed: 340000, avgSalePriceAed: 7000000 },
  // Villanova sub-communities
  { name: 'Amaranta (Villanova)', parentCommunity: 'Villanova', lat: 25.0860, lng: 55.3680, propertyType: '4 Bed Townhouse', avgAnnualRentAed: 225000, avgSalePriceAed: 3800000 },
  { name: 'La Rosa (Villanova)', parentCommunity: 'Villanova', lat: 25.0820, lng: 55.3620, propertyType: '4 Bed Townhouse', avgAnnualRentAed: 215000, avgSalePriceAed: 3600000 },
  // Mudon sub-communities
  { name: 'Mudon Al Ranim', parentCommunity: 'Mudon', lat: 25.0300, lng: 55.2760, propertyType: '4 Bed Villa', avgAnnualRentAed: 285000, avgSalePriceAed: 5100000 },
  // The Valley sub-communities
  { name: 'Alana (The Valley)', parentCommunity: 'The Valley', lat: 25.0020, lng: 55.4420, propertyType: '4 Bed Villa', avgAnnualRentAed: 280000, avgSalePriceAed: 5300000 },
  { name: 'Elora (The Valley)', parentCommunity: 'The Valley', lat: 24.9960, lng: 55.4360, propertyType: '4 Bed Villa', avgAnnualRentAed: 270000, avgSalePriceAed: 5100000 },
  // DAMAC Lagoons sub-communities
  { name: 'Venice (DAMAC Lagoons)', parentCommunity: 'DAMAC Lagoons', lat: 25.0060, lng: 55.2520, propertyType: '4 Bed Townhouse', avgAnnualRentAed: 245000, avgSalePriceAed: 4200000 },
  { name: 'Malta (DAMAC Lagoons)', parentCommunity: 'DAMAC Lagoons', lat: 25.0000, lng: 55.2460, propertyType: '4 Bed Townhouse', avgAnnualRentAed: 235000, avgSalePriceAed: 4000000 },
  // Emaar South sub-communities
  { name: 'Fairway Villas (Emaar South)', parentCommunity: 'Emaar South', lat: 24.8600, lng: 55.1700, propertyType: '4 Bed Villa', avgAnnualRentAed: 240000, avgSalePriceAed: 4200000 },
  { name: 'Golf Lane (Emaar South)', parentCommunity: 'Emaar South', lat: 24.8560, lng: 55.1640, propertyType: '4 Bed Villa', avgAnnualRentAed: 235000, avgSalePriceAed: 4100000 },
  // Dubai South sub-communities
  { name: 'The Pulse (Dubai South)', parentCommunity: 'Dubai South', lat: 24.9060, lng: 55.1640, propertyType: '4 Bed Townhouse', avgAnnualRentAed: 220000, avgSalePriceAed: 3800000 },
  { name: 'South Bay (Dubai South)', parentCommunity: 'Dubai South', lat: 24.8980, lng: 55.1580, propertyType: '4 Bed Villa', avgAnnualRentAed: 280000, avgSalePriceAed: 5000000 },
];

// ─────────────────────────────────────────────────────────────────────────────
// ALIAS NORMALISATION MAP
// Maps known alias/variant names to their canonical community name.
// Used in getClosestCommunitiesWithPricesAll to prevent the same residential
// area appearing twice under slightly different names.
// ─────────────────────────────────────────────────────────────────────────────
const COMMUNITY_ALIASES: Record<string, string> = {
  // Emirates Living sub-communities that duplicate COMMUNITY_PRICES entries
  'the meadows':                       'The Meadows',
  'meadows':                           'The Meadows',
  'the springs':                       'The Springs',
  'springs':                           'The Springs',
  'the lakes':                         'The Lakes',
  'lakes':                             'The Lakes',
  'emirates hills':                    'Emirates Hills',
  // Arabian Ranches variants
  'arabian ranches 1':                 'Arabian Ranches',
  'arabian ranches i':                 'Arabian Ranches',
  // Dubai Hills variants
  'dubai hills':                       'Dubai Hills Estate',
  'dubai hills estate':                'Dubai Hills Estate',
  // JVC / JVT full names
  'jvc':                               'Jumeirah Village Circle (JVC)',
  'jumeirah village circle':           'Jumeirah Village Circle (JVC)',
  'jvt':                               'Jumeirah Village Triangle (JVT)',
  'jumeirah village triangle':         'Jumeirah Village Triangle (JVT)',
  // MBR City variants
  'mohammed bin rashid city':          'MBR City',
  'mbr city':                          'MBR City',
  // Muhaisnah variants
  'al muhaisnah':                      'Muhaisnah',
  'muhaisnah':                         'Muhaisnah',
  // Pearl Jumeirah variants
  'pearl jumeira':                     'Pearl Jumeirah',
  'pearl jumeirah':                    'Pearl Jumeirah',
  // Studio City variants
  'dubai studio city':                 'Studio City',
  'studio city':                       'Studio City',
  // Nad Al Sheba Gardens (also in SUB_COMMUNITIES)
  'nad al sheba gardens':              'Nad Al Sheba Gardens',
  // District One variants
  'district one':                      'District One',
  'district one (mbr city)':           'District One',
  // Sobha Hartland variants
  'sobha hartland':                    'Sobha Hartland',
  'sobha hartland (mbr city)':         'Sobha Hartland',
};

/**
 * Returns the canonical community name for deduplication purposes.
 * Lowercases and trims the input, then looks up in COMMUNITY_ALIASES.
 */
function canonicalName(name: string): string {
  const key = name.toLowerCase().trim();
  return COMMUNITY_ALIASES[key] ?? name;
}

// All communities (master + sub) for commute search
export const ALL_COMMUNITIES_FOR_SEARCH: Array<CommunityPrice & { isSubCommunity?: boolean; parentCommunity?: string }> = [
  ...COMMUNITY_PRICES.map(c => ({ ...c, isSubCommunity: false })),
  ...SUB_COMMUNITIES.map(s => ({
    name: s.name,
    parentCommunity: s.parentCommunity,
    propertyType: s.propertyType,
    avgAnnualRentAed: s.avgAnnualRentAed,
    avgSalePriceAed: s.avgSalePriceAed,
    lat: s.lat,
    lng: s.lng,
    isSubCommunity: true,
  })),
];

// All community names (master + sub) for dropdowns
export const ALL_COMMUNITY_NAMES: string[] = ALL_COMMUNITIES_FOR_SEARCH.map(c => c.name);

/**
 * Get N closest communities with prices to a school location.
 * Uses all communities including sub-communities.
 *
 * Deduplication strategy (v6):
 *   1. Name-exact deduplication: communities with identical names are collapsed.
 *   2. Alias normalisation: known aliases (e.g. "The Meadows" in SUB_COMMUNITIES
 *      vs "The Meadows" in COMMUNITY_PRICES) are resolved to one canonical entry.
 *   3. Straight-line distance sanity check: after sorting by commute time, any
 *      community whose straight-line distance is more than SANITY_RATIO times the
 *      straight-line distance of the closest community is flagged as anomalous.
 *      Anomalous communities are only included if their commute time is genuinely
 *      longer than the closest community's commute time (i.e. the model already
 *      penalised them correctly). This prevents a distant community with a
 *      miscalculated fast commute from appearing above obviously closer ones.
 */
export function getClosestCommunitiesWithPricesAll(
  schoolLat: number,
  schoolLng: number,
  count: number = 20
): (CommunityPrice & { travelMins: number; isSubCommunity?: boolean; parentCommunity?: string })[] {
  // Step 1: Calculate commute times and straight-line distances for all communities
  const withTimes = ALL_COMMUNITIES_FOR_SEARCH.map((c) => ({
    ...c,
    travelMins: estimatePeakTravelMinsFromCommunity(c, schoolLat, schoolLng),
    straightKm: haversineKm(c.lat, c.lng, schoolLat, schoolLng),
  }));

  // Step 2: Sort by commute time (primary ranking)
  withTimes.sort((a, b) => a.travelMins - b.travelMins);

  // Step 3: Deduplicate by canonical name
  // The first occurrence (lowest commute time) wins for each canonical name.
  const seenCanonical = new Set<string>();
  const deduplicated = withTimes.filter((c) => {
    const canon = canonicalName(c.name).toLowerCase().trim();
    if (seenCanonical.has(canon)) return false;
    seenCanonical.add(canon);
    return true;
  });

  // Step 4: Straight-line distance sanity check
  // Find the straight-line distance of the closest community (after dedup).
  // Any community that is more than SANITY_RATIO times farther (straight-line)
  // than the closest community, but has a commute time that is NOT proportionally
  // longer, is an anomaly — it should not rank above genuinely closer communities.
  //
  // We implement this as a re-sort: after dedup, we compute a "sanity-adjusted score"
  // that blends commute time with a distance penalty for anomalous entries.
  // This preserves the primary commute-time ranking for normal entries while
  // pushing anomalous entries down the list.
  const SANITY_RATIO = 3.0; // a community >3× farther (straight-line) than the closest
  const MIN_CLOSE_KM = 1.0; // ignore sanity check if closest community is < 1km away

  if (deduplicated.length > 0) {
    const closestKm = deduplicated[0].straightKm;

    if (closestKm >= MIN_CLOSE_KM) {
      // For each community, compute an anomaly penalty.
      // If a community is more than SANITY_RATIO × closestKm away (straight-line)
      // but its commute time is less than what the distance floor would suggest,
      // it is anomalous. We push it down by inflating its effective score.
      const withScores = deduplicated.map((c) => {
        const distanceRatio = c.straightKm / closestKm;
        const distFloor = distanceBasedMinimum(c.straightKm);
        const isAnomalous = distanceRatio > SANITY_RATIO && c.travelMins < distFloor * 0.85;
        // Anomalous entries get their score inflated to the distance floor
        const effectiveScore = isAnomalous ? Math.max(c.travelMins, distFloor) : c.travelMins;
        return { ...c, effectiveScore };
      });

      // Re-sort by effective score (anomalous entries pushed down)
      withScores.sort((a, b) => a.effectiveScore - b.effectiveScore);

      return withScores
        .slice(0, count)
        .map(({ effectiveScore: _es, straightKm: _sk, ...rest }) => rest);
    }
  }

  // Fallback: return deduplicated list without sanity adjustment
  return deduplicated
    .slice(0, count)
    .map(({ straightKm: _sk, ...rest }) => rest);
}
